import React, { useState } from "react";
import {
  CheckCircleFilled,
  HeartFilled,
  LeftOutlined,
  LinkOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  formatPaymentExchangeRate,
  formatPaymentIls,
  formatPaymentUsd,
  resolvePaymentUsdFromIls,
} from "./formatPaymentIls";
import "../packagesPaymentMockup/PackagesPaymentMockupPage.css";

function PpmFinanceAmounts({ ilsAmount, ilsToUsd = null, currencyRate = null, emphasis = false }) {
  const usdRaw = resolvePaymentUsdFromIls(ilsAmount, ilsToUsd, currencyRate);
  const usdLabel = formatPaymentUsd(usdRaw);
  const ilsLabel = formatPaymentIls(ilsAmount);
  return (
    <div
      className={`ppm-finance__amounts${emphasis ? " ppm-finance__amounts--emphasis" : ""}`}
      dir="ltr"
    >
      {usdLabel ? (
        <strong className="ppm-finance__usd">{usdLabel}</strong>
      ) : null}
      <strong className="ppm-finance__ils">{ilsLabel}</strong>
    </div>
  );
}

const PPM_TRUST_ITEMS = [
  { id: "service", label: "שירות אישי" },
  { id: "secure", label: "הזמנה מאובטחת" },
  { id: "price", label: "מחירים תחרותיים" },
];

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <div className="ppm-field-error" id={id} role="alert">
      {message}
    </div>
  );
}

function PpmChromeShell({
  brandLogo,
  brandAlt,
  brandName,
  thanksTeam,
  onBack,
  isPublic,
  demoBar,
  children,
}) {
  return (
    <div className="ppm-mock" dir="rtl" lang="he">
      {demoBar}
      <header className="ppm-topbar">
        <div className="ppm-shell">
          <div className="ppm-topbar__inner">
            <div className="ppm-topbar__brand">
              {brandLogo ? (
                <img src={brandLogo} alt={brandAlt} />
              ) : (
                <strong className="ppm-topbar__brand-text">{brandAlt}</strong>
              )}
            </div>
            {onBack ? (
              <button type="button" className="ppm-back" onClick={onBack}>
                <LeftOutlined />
                {isPublic ? "חזרה להצעה" : "חזרה לחבילה"}
              </button>
            ) : null}
          </div>
        </div>
      </header>
      {children}
      <footer className="ppm-footer">
        <div className="ppm-shell">
          <div className="ppm-footer__row">
            <div className="ppm-footer__brand">
              {brandLogo ? (
                <img
                  src={brandLogo}
                  alt={brandName || brandAlt || "לוגו סוכנות"}
                />
              ) : brandName || brandAlt ? (
                <span className="ppm-footer__brand-name">
                  {brandName || brandAlt}
                </span>
              ) : null}
            </div>
            <ul className="ppm-footer__trust" aria-label="יתרונות">
              {PPM_TRUST_ITEMS.map((t) => (
                <li key={t.id}>
                  <SafetyCertificateOutlined aria-hidden />
                  <span>{t.label}</span>
                </li>
              ))}
            </ul>
            <div className="ppm-footer__thanks">
              <HeartFilled aria-hidden className="ppm-footer__thanks-icon" />
              <div className="ppm-footer__thanks-copy">
                <strong>תודה שבחרתם בנו!</strong>
                <span>{thanksTeam}</span>
              </div>
            </div>
          </div>
          <div className="ppm-footer__legal">
            <nav className="ppm-footer__links" aria-label="תקנונים">
              {!isPublic ? (
                <>
                  <a href="/agentTerms" target="_blank" rel="noopener noreferrer">
                    תקנון סוכנים
                  </a>
                  <span className="ppm-footer__sep" aria-hidden>
                    ·
                  </span>
                </>
              ) : null}
              <a
                href="/termsAndConditions"
                target="_blank"
                rel="noopener noreferrer"
              >
                תקנון הזמנה
              </a>
            </nav>
            <p className="ppm-footer__copy">
              © {new Date().getFullYear()} {brandName || "HotelsRiver"} · כל
              הזכויות שמורות
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Shared LDS package payment chrome (mockup SSOT).
 * Mockup + live PaymentAgent feed the same view; live keeps real handlers via callbacks/slots.
 */
export default function PackagePaymentView({
  order,
  financial,
  brand = null,
  viewerMode = "agent",
  uiState = "idle",
  method = "credit",
  payPath = "link",
  cards = [],
  activeCard = 0,
  cardCount = 1,
  termsOk = false,
  fieldErrors = {},
  paymentLink = null,
  actionError = null,
  busy = false,
  detailsOpen: detailsOpenControlled = null,
  onDetailsOpenChange = null,
  onBack = null,
  onMethodChange = null,
  onPayPathChange = null,
  onCardCountChange = null,
  onActiveCardChange = null,
  onCardAmountChange = null,
  onInstallmentsChange = null,
  onTermsChange = null,
  onPrimaryAction = null,
  onCopyLink = null,
  onCloseGateway = null,
  onRetry = null,
  onSuccessContinue = null,
  termsHref = null,
  onTermsLinkClick = null,
  bankSlot = null,
  gatewaySlot = null,
  engineSlot = null,
  settlementSlot = null,
  afterPaySlot = null,
  toast = null,
  /** Agent-only chrome (pay-path tabs, agent name in details). */
  isPublic = false,
  /** When true, credit tab is disabled (e.g. bank transfer already pending). */
  lockMethodToBank = false,
  errorTitle = "שגיאה בטעינת ההזמנה",
  errorMessage = "לא ניתן לטעון את פרטי התשלום. נסו שוב או חזרו לדשבורד.",
  successTitle = "התשלום הושלם",
  successMessage = null,
  successPrimaryLabel = "חזרה",
  loadingMessage = "טוען נתונים…",
  demoBar = null,
  /** Optional ILS→USD converter for summary dual-currency. */
  ilsToUsd = null,
  currencyRate = null,
  /** Optional explicit ILS-per-unit rate at payment time (overrides currencyRate.USD). */
  paymentExchangeRate = null,
  /** Supplier currency for that rate. Omitted keeps the legacy 1$ label. */
  paymentExchangeCurrency = null,
}) {
  const [detailsOpenLocal, setDetailsOpenLocal] = useState(false);
  const detailsOpen =
    detailsOpenControlled != null ? detailsOpenControlled : detailsOpenLocal;
  const setDetailsOpen = (next) => {
    const value = typeof next === "function" ? next(detailsOpen) : next;
    if (onDetailsOpenChange) onDetailsOpenChange(value);
    else setDetailsOpenLocal(value);
  };

  const isAgent = viewerMode === "agent" && !isPublic;
  const fullyPaid = !!financial?.isFullyPaid;
  const remaining = financial?.remainingBalance;
  const chargeNow = cards[activeCard]?.amount;
  const heldAmount =
    financial?.heldAmount != null && Number(financial.heldAmount) > 0
      ? Number(financial.heldAmount)
      : null;
  const paidActual =
    financial?.paidAmount != null && Number.isFinite(Number(financial.paidAmount))
      ? Number(financial.paidAmount)
      : null;

  const effectivePath = isAgent ? payPath : "payNow";
  const amountLabel =
    effectivePath === "link" ? "סכום לתשלום בקישור" : "סכום לחיוב עכשיו";
  const primaryLabel =
    effectivePath === "link"
      ? `יצירת קישור תשלום${chargeNow != null ? ` · ${formatPaymentIls(chargeNow)}` : ""}`
      : `שלם עכשיו${chargeNow != null ? ` · ${formatPaymentIls(chargeNow)}` : ""}`;

  const installmentOptions = Array.from({ length: 12 }, (_, i) => i + 1);
  const brandLogo = brand?.logoUrl || null;
  const brandAlt = brand?.alt || brand?.name || "סוכנות הנסיעות";
  const brandName = brand?.name || brandAlt;
  const thanksTeam =
    (brand?.thanksTeam && String(brand.thanksTeam).trim()) ||
    (brandName ? `צוות ${brandName}` : "צוות הסוכנות");
  const isBusy = busy || uiState === "loading" || uiState === "processing";
  const exchangeRateLabel = formatPaymentExchangeRate(
    paymentExchangeRate ?? currencyRate,
    paymentExchangeCurrency
  );
  const passengerNames = Array.isArray(order?.passengers)
    ? order.passengers.map((p) => p?.name || "").filter(Boolean)
    : [];
  const agentLabel = String(order?.agentName || "").trim();
  const showAgentInDetails =
    isAgent && agentLabel && !/^hotels\s*river$/i.test(agentLabel);
  const hasAddress = !!String(order?.address || "").trim();
  const hasExtraDetails = hasAddress || showAgentInDetails;
  const chromeProps = {
    brandLogo,
    brandAlt,
    brandName,
    thanksTeam,
    onBack,
    isPublic,
    demoBar,
  };

  if (uiState === "loading") {
    return (
      <PpmChromeShell {...chromeProps}>
        <div className="ppm-shell" role="status" aria-busy="true" aria-label={loadingMessage}>
          <div className="ppm-page-head">
            <div className="ppm-skel ppm-skel--title" />
            <div className="ppm-skel ppm-skel--line ppm-skel--line-sm" />
          </div>
          <div className="ppm-layout ppm-layout--skeleton">
            <main className="ppm-main">
              <section className="ppm-pay ppm-pay--skeleton" aria-hidden>
                <div className="ppm-skel ppm-skel--section-title" />
                <div className="ppm-skel-segment">
                  <span className="ppm-skel ppm-skel--pill" />
                  <span className="ppm-skel ppm-skel--pill" />
                </div>
                <div className="ppm-skel ppm-skel--section-title" />
                <div className="ppm-skel-segment">
                  <span className="ppm-skel ppm-skel--pill" />
                  <span className="ppm-skel ppm-skel--pill" />
                </div>
                <div className="ppm-skel-fields">
                  <span className="ppm-skel ppm-skel--field" />
                  <span className="ppm-skel ppm-skel--field" />
                </div>
                <div className="ppm-skel ppm-skel--terms" />
                <div className="ppm-skel ppm-skel--cta" />
              </section>
            </main>
            <aside className="ppm-summary" aria-hidden>
              <div className="ppm-summary__card ppm-summary__card--skeleton">
                <div className="ppm-skel-hotel">
                  <span className="ppm-skel ppm-skel--thumb" />
                  <div className="ppm-skel-hotel__text">
                    <span className="ppm-skel ppm-skel--line" />
                    <span className="ppm-skel ppm-skel--line ppm-skel--line-sm" />
                  </div>
                </div>
                <div className="ppm-skel-facts">
                  <span className="ppm-skel ppm-skel--line" />
                  <span className="ppm-skel ppm-skel--line" />
                  <span className="ppm-skel ppm-skel--line" />
                </div>
                <div className="ppm-skel ppm-skel--finance" />
              </div>
            </aside>
          </div>
          <p className="ppm-skel-hint">
            <LoadingOutlined spin />
            <span>{loadingMessage}</span>
          </p>
        </div>
        {engineSlot}
      </PpmChromeShell>
    );
  }

  if (uiState === "error") {
    return (
      <PpmChromeShell {...chromeProps}>
        <div className="ppm-state ppm-state--error" role="alert">
          <WarningOutlined />
          <h1>{errorTitle}</h1>
          <p>{errorMessage}</p>
          {onRetry ? (
            <button type="button" className="ppm-btn-secondary" onClick={onRetry}>
              נסה שוב
            </button>
          ) : null}
        </div>
        {engineSlot}
      </PpmChromeShell>
    );
  }

  if (uiState === "success") {
    return (
      <PpmChromeShell {...chromeProps}>
        <div className="ppm-state ppm-state--success">
          <CheckCircleFilled />
          <h1>{successTitle}</h1>
          {successMessage ? <p>{successMessage}</p> : null}
          {onSuccessContinue ? (
            <button type="button" className="ppm-btn-primary" onClick={onSuccessContinue}>
              {successPrimaryLabel}
            </button>
          ) : null}
        </div>
        {engineSlot}
      </PpmChromeShell>
    );
  }

  const summaryCard = (
    <aside className="ppm-summary" aria-label="סיכום הזמנה">
      <div className="ppm-summary__card">
        <div className="ppm-summary__hotel">
          {order?.imageUrl ? (
            <img
              className="ppm-summary__thumb"
              src={order.imageUrl}
              alt=""
              width={88}
              height={72}
            />
          ) : null}
          <div className="ppm-summary__hotel-text">
            <strong dir="auto">{order?.hotelName || "—"}</strong>
            <span>
              {[order?.city, order?.country].filter(Boolean).join(", ") || "—"}
            </span>
          </div>
        </div>

        <ul className="ppm-summary__facts">
          <li>
            <span className="ppm-summary__fact-label">תאריכים</span>
            <span className="ppm-summary__fact-value" dir="ltr">
              {order?.checkIn || "—"} – {order?.checkOut || "—"}
            </span>
            {order?.nights != null ? (
              <span className="ppm-summary__fact-meta">{order.nights} לילות</span>
            ) : null}
          </li>
          <li>
            <span className="ppm-summary__fact-label">נוסעים וחדרים</span>
            <span className="ppm-summary__fact-value">
              {order?.passengersCount ?? "—"} נוסעים · {order?.roomsCount ?? "—"}{" "}
              {Number(order?.roomsCount) === 1 ? "חדר" : "חדרים"}
            </span>
            {passengerNames.length ? (
              <span className="ppm-summary__fact-passengers">
                {passengerNames.join(", ")}
              </span>
            ) : null}
          </li>
          <li>
            <span className="ppm-summary__fact-label">סוג חדר</span>
            <span className="ppm-summary__fact-value">
              {order?.roomType || "—"}
            </span>
          </li>
          {order?.boardLabel ? (
            <li>
              <span className="ppm-summary__fact-label">בסיס אירוח</span>
              <span className="ppm-summary__fact-value">{order.boardLabel}</span>
            </li>
          ) : null}
        </ul>

        {Array.isArray(order?.baggageItems) && order.baggageItems.length ? (
          <div className="ppm-summary__baggage" aria-label="פירוט כבודה">
            <p className="ppm-summary__baggage-title">כבודה</p>
            <ul className="ppm-summary__baggage-list">
              {order.baggageItems.map((item, idx) => (
                <li
                  key={`${item.direction || "bag"}-${item.title}-${idx}`}
                  className="ppm-summary__baggage-row"
                >
                  {item.direction ? (
                    <span className="ppm-summary__baggage-dir">
                      {item.direction}
                    </span>
                  ) : null}
                  <span className="ppm-summary__baggage-main">
                    <strong>{item.title}</strong>
                    {item.paxCount > 0 ? (
                      <span>· {item.paxCount} נוסעים</span>
                    ) : null}
                  </span>
                  {item.priceLabel ? (
                    <span className="ppm-summary__baggage-price" dir="ltr">
                      {item.priceLabel}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : order?.baggageLabel ? (
          <div className="ppm-summary__baggage" aria-label="פירוט כבודה">
            <p className="ppm-summary__baggage-title">כבודה</p>
            <p className="ppm-summary__baggage-fallback">{order.baggageLabel}</p>
          </div>
        ) : null}

        {order?.flight?.outbound || order?.flight?.inbound ? (
          <div className="ppm-summary__flights" aria-label="פירוט טיסות">
            <p className="ppm-summary__flights-title">פירוט טיסות</p>
            <ul className="ppm-summary__facts ppm-summary__facts--flights">
              {order.flight?.outbound ? (
                <li>
                  <span className="ppm-summary__fact-label">הלוך</span>
                  <span className="ppm-summary__fact-value" dir="ltr">
                    {[
                      order.flight.outbound.depart,
                      order.flight.outbound.from,
                      "→",
                      order.flight.outbound.arrive,
                      order.flight.outbound.to,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                  <span className="ppm-summary__fact-meta">
                    {[
                      order.flight.outbound.airline,
                      order.flight.outbound.flightNo,
                      order.flight.outbound.duration,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </span>
                </li>
              ) : null}
              {order.flight?.inbound ? (
                <li>
                  <span className="ppm-summary__fact-label">חזור</span>
                  <span className="ppm-summary__fact-value" dir="ltr">
                    {[
                      order.flight.inbound.depart,
                      order.flight.inbound.from,
                      "→",
                      order.flight.inbound.arrive,
                      order.flight.inbound.to,
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  </span>
                  <span className="ppm-summary__fact-meta">
                    {[
                      order.flight.inbound.airline,
                      order.flight.inbound.flightNo,
                      order.flight.inbound.duration,
                    ]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </span>
                </li>
              ) : null}
            </ul>
          </div>
        ) : order?.flightLabel ? (
          <div className="ppm-summary__flights" aria-label="פירוט טיסות">
            <p className="ppm-summary__flights-title">פירוט טיסות</p>
            <ul className="ppm-summary__facts ppm-summary__facts--flights">
              <li>
                <span className="ppm-summary__fact-label">טיסה</span>
                <span className="ppm-summary__fact-value">{order.flightLabel}</span>
              </li>
            </ul>
          </div>
        ) : null}

        {hasExtraDetails ? (
          <>
            <button
              type="button"
              className="ppm-details-link"
              aria-expanded={detailsOpen}
              onClick={() => setDetailsOpen((v) => !v)}
            >
              {detailsOpen ? "הסתר פרטי ההזמנה" : "פרטי ההזמנה"}
            </button>
            {detailsOpen ? (
              <dl className="ppm-details">
                {hasAddress ? (
                  <div>
                    <dt>כתובת</dt>
                    <dd dir="auto">{order.address}</dd>
                  </div>
                ) : null}
                {showAgentInDetails ? (
                  <div>
                    <dt>סוכן</dt>
                    <dd>{agentLabel}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </>
        ) : null}

        <div className="ppm-finance" aria-label="סיכום כספי">
          {exchangeRateLabel ? (
            <div className="ppm-finance__row ppm-finance__row--rate">
              <span>שער מטבע לתשלום</span>
              <strong className="ppm-finance__rate" dir="ltr">
                {exchangeRateLabel}
              </strong>
            </div>
          ) : null}
          <div className="ppm-finance__row">
            <span>סה״כ ההזמנה</span>
            <PpmFinanceAmounts
              ilsAmount={financial?.totalAmount}
              ilsToUsd={ilsToUsd}
              currencyRate={currencyRate}
            />
          </div>
          {paidActual != null && paidActual > 0 ? (
            <div className="ppm-finance__row">
              <span>שולם בפועל</span>
              <PpmFinanceAmounts
                ilsAmount={paidActual}
                ilsToUsd={ilsToUsd}
                currencyRate={currencyRate}
              />
            </div>
          ) : null}
          {heldAmount != null ? (
            <div className="ppm-finance__row">
              <span>תפיסת מסגרת</span>
              <PpmFinanceAmounts
                ilsAmount={heldAmount}
                ilsToUsd={ilsToUsd}
                currencyRate={currencyRate}
              />
            </div>
          ) : null}
          <div className="ppm-finance__row ppm-finance__row--emphasis">
            <span>יתרה לתשלום</span>
            <PpmFinanceAmounts
              ilsAmount={financial?.remainingBalance}
              ilsToUsd={ilsToUsd}
              currencyRate={currencyRate}
              emphasis
            />
          </div>
        </div>
      </div>
    </aside>
  );

  return (
    <PpmChromeShell {...chromeProps}>
      <div className="ppm-shell">
        <div className="ppm-page-head">
          <h1>תשלום ההזמנה</h1>
          <p>
            מספר הזמנה{" "}
            <span dir="ltr">{order?.orderNumber || "—"}</span>
          </p>
        </div>

        <div className="ppm-layout">
          <main className="ppm-main">
            <section className="ppm-pay" aria-label="אזור תשלום">
              {fullyPaid ? (
                <div className="ppm-banner ppm-banner--ok" role="status">
                  <CheckCircleFilled />
                  ההזמנה שולמה במלואה — לא ניתן להוסיף תשלום
                </div>
              ) : null}

              {settlementSlot}

              <div className="ppm-section">
                <h2 className="ppm-section__title">אמצעי תשלום</h2>
                <div className="ppm-segment" role="tablist" aria-label="אמצעי תשלום">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={method === "credit"}
                    className={method === "credit" ? "is-active" : ""}
                    onClick={() => onMethodChange?.("credit")}
                    disabled={isBusy || fullyPaid || lockMethodToBank}
                  >
                    כרטיס אשראי
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={method === "bank"}
                    className={method === "bank" ? "is-active" : ""}
                    onClick={() => onMethodChange?.("bank")}
                    disabled={isBusy || fullyPaid}
                  >
                    העברה בנקאית
                  </button>
                </div>
              </div>

              {method === "bank" ? (
                <div className="ppm-section">
                  <h2 className="ppm-section__title">העברה בנקאית</h2>
                  {bankSlot || (
                    <>
                      <p className="ppm-section__text">
                        השלימו את ההעברה לפי ההוראות בעמוד.
                      </p>
                      <p className="ppm-section__meta">
                        יתרה לתשלום:{" "}
                        <strong dir="ltr">{formatPaymentIls(remaining)}</strong>
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {isAgent ? (
                    <div className="ppm-section">
                      <h2 className="ppm-section__title">אופן התשלום</h2>
                      <div className="ppm-segment" role="tablist" aria-label="אופן התשלום">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={payPath === "link"}
                          className={payPath === "link" ? "is-active" : ""}
                          onClick={() => onPayPathChange?.("link")}
                          disabled={isBusy || fullyPaid}
                        >
                          שליחת קישור ללקוח
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={payPath === "payNow"}
                          className={payPath === "payNow" ? "is-active" : ""}
                          onClick={() => onPayPathChange?.("payNow")}
                          disabled={isBusy || fullyPaid}
                        >
                          תשלום עכשיו
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <div className="ppm-section">
                    <h2 className="ppm-section__title">הגדרת התשלום</h2>

                    <div className="ppm-split-row">
                      <label htmlFor="ppm-card-count">פיצול בין כרטיסים</label>
                      <select
                        id="ppm-card-count"
                        value={cardCount}
                        disabled={isBusy || fullyPaid}
                        onChange={(e) =>
                          onCardCountChange?.(Number(e.target.value) || 1)
                        }
                      >
                        {[1, 2, 3, 4].map((n) => (
                          <option key={n} value={n}>
                            {n === 1 ? "כרטיס אחד" : `${n} כרטיסים`}
                          </option>
                        ))}
                      </select>
                    </div>

                    {cards.length > 1 ? (
                      <div className="ppm-card-tabs" role="tablist" aria-label="כרטיסים">
                        {cards.map((c, i) => {
                          const done = !!c.paid || !!c.locked;
                          return (
                            <button
                              key={c.id || `card-${i}`}
                              type="button"
                              role="tab"
                              aria-selected={activeCard === i}
                              className={[
                                activeCard === i ? "is-active" : "",
                                done ? "is-done" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              disabled={isBusy || fullyPaid}
                              onClick={() => onActiveCardChange?.(i)}
                            >
                              <span className="ppm-card-tabs__title">
                                כרטיס {i + 1}
                                {done ? " · שולם" : ""}
                              </span>
                              <span className="ppm-card-tabs__amount" dir="ltr">
                                {formatPaymentIls(c.amount)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    ) : null}

                    <div className="ppm-fields-row">
                      <label className="ppm-field" htmlFor="ppm-amount">
                        <span>הסכום בכרטיס זה (₪)</span>
                        <input
                          id="ppm-amount"
                          type="number"
                          min={0}
                          step={1}
                          dir="ltr"
                          disabled={
                            isBusy || fullyPaid || !!cards[activeCard]?.paid
                          }
                          value={cards[activeCard]?.amount ?? ""}
                          aria-invalid={!!fieldErrors.amount}
                          aria-describedby={
                            fieldErrors.amount ? "ppm-amount-err" : undefined
                          }
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            onCardAmountChange?.(
                              Number.isFinite(n) ? n : null
                            );
                          }}
                        />
                        <FieldError id="ppm-amount-err" message={fieldErrors.amount} />
                      </label>

                      <label className="ppm-field" htmlFor="ppm-tash">
                        <span>מספר תשלומים</span>
                        <select
                          id="ppm-tash"
                          disabled={
                            isBusy || fullyPaid || !!cards[activeCard]?.paid
                          }
                          value={
                            cards[activeCard]?.numberOfPayments ||
                            cards[activeCard]?.numberofpayments ||
                            1
                          }
                          onChange={(e) =>
                            onInstallmentsChange?.(Number(e.target.value) || 1)
                          }
                        >
                          {installmentOptions.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  </div>

                  <div className="ppm-action-block">
                    <div className="ppm-amount-line" aria-live="polite">
                      <span>{amountLabel}</span>
                      <strong dir="ltr">{formatPaymentIls(chargeNow)}</strong>
                    </div>

                    <label className="ppm-terms">
                      <input
                        type="checkbox"
                        checked={termsOk}
                        disabled={isBusy || fullyPaid}
                        onChange={(e) => onTermsChange?.(e.target.checked)}
                        aria-invalid={!!fieldErrors.terms}
                        aria-describedby={
                          fieldErrors.terms ? "ppm-terms-err" : undefined
                        }
                      />
                      <span>
                        אני מאשר/ת את{" "}
                        {termsHref ? (
                          <a
                            href={termsHref}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={onTermsLinkClick}
                          >
                            התקנון ותנאי הביטול
                          </a>
                        ) : (
                          <a
                            href="#terms"
                            onClick={(e) => {
                              e.preventDefault();
                              onTermsLinkClick?.(e);
                            }}
                          >
                            התקנון ותנאי הביטול
                          </a>
                        )}
                      </span>
                    </label>
                    <FieldError id="ppm-terms-err" message={fieldErrors.terms} />
                    {fieldErrors.form ? (
                      <p className="ppm-field-error" role="alert">
                        {fieldErrors.form}
                      </p>
                    ) : null}
                    {actionError ? (
                      <p className="ppm-field-error" role="alert">
                        {actionError}
                      </p>
                    ) : null}

                    <button
                      type="button"
                      className="ppm-btn-primary ppm-btn-primary--block"
                      disabled={isBusy || fullyPaid || !termsOk}
                      onClick={onPrimaryAction}
                    >
                      {uiState === "processing" ? (
                        <>
                          <LoadingOutlined spin /> יוצר קישור…
                        </>
                      ) : (
                        primaryLabel
                      )}
                    </button>

                    {paymentLink &&
                    (uiState === "linkReady" ||
                      (effectivePath === "link" &&
                        uiState !== "gateway" &&
                        uiState !== "processing")) ? (
                      <div className="ppm-link-box">
                        <h3>קישור תשלום מוכן</h3>
                        <p className="ppm-section__meta">
                          ניתן להעתיק את הקישור ולשלוח ללקוח.
                        </p>
                        <div className="ppm-link-row">
                          <input
                            type="text"
                            readOnly
                            value={
                              typeof paymentLink === "string"
                                ? paymentLink
                                : paymentLink?.paymentUrl ||
                                  paymentLink?.url ||
                                  paymentLink?.payment_url ||
                                  ""
                            }
                            dir="ltr"
                            aria-label="קישור תשלום"
                          />
                          <button
                            type="button"
                            className="ppm-btn-secondary"
                            onClick={onCopyLink}
                          >
                            <LinkOutlined /> העתק
                          </button>
                        </div>
                      </div>
                    ) : null}

                    {uiState === "gateway" ? (
                      <div className="ppm-gateway" role="region" aria-label="חלון סליקה">
                        {gatewaySlot || (
                          <>
                            <div className="ppm-gateway__badge">סליקה מאובטחת</div>
                            <h3>חלון סליקה</h3>
                            <p className="ppm-section__text">
                              חלון התשלום נפתח אצל ספק הסליקה. אם נחסם חלון קופץ,
                              השתמשו בקישור למטה.
                            </p>
                            <div className="ppm-gateway__frame" aria-hidden>
                              <span>אזור iframe / דף ספק</span>
                            </div>
                            {onCloseGateway ? (
                              <div className="ppm-gateway__actions">
                                <button
                                  type="button"
                                  className="ppm-btn-ghost"
                                  onClick={onCloseGateway}
                                >
                                  סגור חלון
                                </button>
                              </div>
                            ) : null}
                          </>
                        )}
                      </div>
                    ) : null}

                    {afterPaySlot}
                  </div>
                </>
              )}
            </section>
          </main>

          {summaryCard}
        </div>
      </div>

      {engineSlot}

      {toast ? (
        <div className="ppm-toast" role="status">
          {toast}
        </div>
      ) : null}
    </PpmChromeShell>
  );
}
